import { useEffect, useState } from 'react';
import type { MerchantProfile } from '@/types/api/merchant';
import { platformApi } from '@/services/platformApi';
import { merchantProfileAwaitingPhotoHydration } from '@/utils/merchantStorefrontGallery';

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 12;

/**
 * Re-fetch public merchant profile while the platform lazy-hydrates Google Place photos.
 */
export function useMerchantProfilePhotoPoll(
  merchant: MerchantProfile | null,
  merchantUserId: string | undefined,
  onRefreshed: (profile: MerchantProfile) => void,
  enabled: boolean
): boolean {
  const [isPollingPhotos, setIsPollingPhotos] = useState(false);
  const awaitingHydration = merchantProfileAwaitingPhotoHydration(merchant);

  useEffect(() => {
    if (!enabled || !merchantUserId || !awaitingHydration) {
      setIsPollingPhotos(false);
      return;
    }

    let cancelled = false;
    let attempts = 0;
    setIsPollingPhotos(true);

    const tick = async () => {
      if (cancelled || attempts >= MAX_POLL_ATTEMPTS) {
        setIsPollingPhotos(false);
        return;
      }
      attempts += 1;

      try {
        const fresh = await platformApi.merchant.getPublicMerchantProfile(merchantUserId);
        if (cancelled) return;
        onRefreshed(fresh);
        if (!merchantProfileAwaitingPhotoHydration(fresh)) {
          setIsPollingPhotos(false);
        }
      } catch {
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setIsPollingPhotos(false);
        }
      }
    };

    void tick();
    const timer = window.setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      setIsPollingPhotos(false);
      window.clearInterval(timer);
    };
  }, [enabled, merchantUserId, awaitingHydration, onRefreshed]);

  return isPollingPhotos;
}
